import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useGetCallDispositionsQuery, useGetCallQuery, useUpdateCallMutation } from "@/store/rest_api";
import { Check, Mic, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { apiErrorMessage } from "@/util/apiError";

interface CallDispositionFormProps {
  callId: string;
  displayName: string;
  onComplete: () => void;
}

export default function CallDispositionForm({ callId, displayName, onComplete }: CallDispositionFormProps) {
  const { data: dispositions = [] } = useGetCallDispositionsQuery({});
  const { data: call } = useGetCallQuery(callId, { skip: !callId });
  const [updateCall] = useUpdateCallMutation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCall({
        id: callId,
        ...(selectedId ? { disposition: { id: selectedId } } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }).unwrap();
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to save the disposition"));
      setSaving(false);
      return;
    }
    setSaving(false);
    onComplete();
  };

  return (
    <div className="flex flex-col items-center h-full space-y-6 py-4">
      <div className="text-center space-y-1">
        <p className="text-sm text-muted-foreground">Call ended</p>
        <p className="text-lg font-semibold dark:text-white">{displayName}</p>
        {call?.recording && (
          <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <Mic className="h-3 w-3" />
            Recorded
          </p>
        )}
      </div>

      <div className="w-full space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Disposition</p>
        <div className="grid grid-cols-2 gap-1.5">
          {dispositions.map((d) => (
            <Button
              key={d.id}
              size="sm"
              variant="outline"
              className={cn(
                "text-xs justify-start gap-2",
                selectedId === d.id && "border-primary bg-primary/10 ring-1 ring-primary hover:bg-primary/10",
              )}
              onClick={() => setSelectedId(selectedId === d.id ? null : d.id)}
            >
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: d.color }} />
              {d.name}
            </Button>
          ))}
        </div>
      </div>

      <div className="w-full space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Notes</p>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add call notes..."
          className="text-sm min-h-[80px] resize-none"
        />
      </div>

      <div className="flex gap-2 w-full">
        <Button variant="outline" className="flex-1" onClick={onComplete}>
          <X className="h-4 w-4 mr-1" />
          Skip
        </Button>
        <Button className="flex-1" onClick={handleSave} disabled={saving}>
          <Check className="h-4 w-4 mr-1" />
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
}
